import React, { useState, useEffect } from "react";
import { X, HardDrive, Sparkles, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { showConfirmDialog } from "../../../components/ConfirmDialog";
import { getDocumentAccessMe } from "../../../api/elearning/documents/documentAccess";

/**
 * Modal xác nhận tải bài giảng (hiển thị thông tin bài học, hạn mức tải trong tháng,
 * và khi bấm "Bắt đầu tải" sẽ chuyển xuống Floating Download Dock góc phải màn hình)
 */
const LessonDownloadModal = ({
  isOpen,
  onClose,
  title = "Tải tài liệu",
  courseName = "—",
  lessonTitle = "—",
  contentLine = "",
  typeLabel = "—",
  typeHighlight = false,
  fileSize = "—",
  localVersionReady = false,
  isAlreadyDownloading = false,
  onStartDownload,
}) => {
  const [accessQuota, setAccessQuota] = useState(null);
  const [loadingQuota, setLoadingQuota] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setLoadingQuota(true);
      getDocumentAccessMe()
        .then((data) => setAccessQuota(data))
        .catch(() => setAccessQuota(null))
        .finally(() => setLoadingQuota(false));
    } else {
      setAccessQuota(null);
    }
  }, [isOpen]);

  const isQuotaExhausted = Boolean(
    accessQuota && !accessQuota.exempt && Number(accessQuota.remaining) <= 0
  );

  const handleStart = async () => {
    if (!onStartDownload || isQuotaExhausted || isAlreadyDownloading) return;

    if (localVersionReady) {
      const confirm = await showConfirmDialog({
        title: "Tải lại bài giảng?",
        text:
          "Thiết bị đã có bản bài giảng đúng phiên bản hiện tại. Tải lại có thể tốn thêm 1 lượt tải trong tháng (nếu tài khoản có giới hạn). Bạn chắc chắn muốn tải lại?",
        confirmText: "Vẫn tải lại",
        cancelText: "Hủy",
      });
      if (!confirm.isConfirmed) return;
    }

    onStartDownload?.();
    onClose?.();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[220] bg-black/50 backdrop-blur-[1px]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="fixed inset-0 z-[230] flex items-center justify-center p-4 pointer-events-none"
          >
            <div
              className="pointer-events-auto w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl border border-gray-100"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between bg-gradient-to-r from-red-600 via-rose-600 to-red-700 px-5 py-3.5 text-white">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/20 text-xs font-black shadow-inner">
                    IMA
                  </div>
                  <span className="text-sm font-extrabold tracking-wide truncate">
                    VIỆT NAM
                  </span>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl p-1.5 hover:bg-white/15 text-white/90 hover:text-white transition"
                  aria-label="Đóng"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Title */}
              <div className="px-6 pt-5 pb-1">
                <h3 className="text-center text-lg font-black text-gray-900">
                  {title}
                </h3>
              </div>

              {/* Thông báo nếu đã có trên máy */}
              {localVersionReady && (
                <div className="mx-6 mt-2 flex items-start gap-2 rounded-2xl border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-xs text-emerald-900">
                  <HardDrive className="h-4 w-4 shrink-0 mt-0.5 text-emerald-700" strokeWidth={2} />
                  <p>
                    <span className="font-bold">Đã có trên thiết bị</span> — phiên bản khớp với máy chủ. Bạn chỉ cần tải lại nếu muốn cập nhật mới.
                  </p>
                </div>
              )}

              {/* Cảnh báo nếu đang tải ngầm */}
              {isAlreadyDownloading && (
                <div className="mx-6 mt-2 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-xs text-amber-900">
                  <Sparkles className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
                  <p>
                    <span className="font-bold">Đang tải ngầm</span> — bài giảng này đang nằm trong hàng đợi tải ở góc dưới màn hình.
                  </p>
                </div>
              )}

              {/* Cảnh báo hết lượt */}
              {isQuotaExhausted && (
                <div className="mx-6 mt-2 flex items-start gap-2 rounded-2xl border border-red-200 bg-red-50 px-3.5 py-2.5 text-xs text-red-900">
                  <AlertCircle className="h-4 w-4 shrink-0 mt-0.5 text-red-600" />
                  <p>
                    <span className="font-bold">Hết lượt tải trong tháng</span> — Bạn đã dùng hết hạn mức tải tài liệu tháng này ({accessQuota?.usedThisMonth} / {accessQuota?.allowance} lượt). Vui lòng liên hệ Admin để được cấp thêm.
                  </p>
                </div>
              )}

              {/* Thông tin bài học & Quota */}
              <div className="space-y-2.5 px-6 py-4 text-sm">
                <Row label="Khóa học" value={courseName} />
                <Row label="Bài học" value={lessonTitle} />
                <Row label="Nội dung" value={contentLine || "—"} />
                <div className="flex gap-2 border-b border-gray-100 py-1.5 items-center">
                  <span className="w-28 shrink-0 text-gray-500 text-xs">Phân loại</span>
                  <span
                    className={`min-w-0 flex-1 font-semibold text-xs ${
                      typeHighlight ? "text-red-700 font-bold" : "text-gray-900"
                    }`}
                  >
                    {typeLabel}
                  </span>
                </div>
                <Row label="Dung lượng" value={fileSize} />

                {/* Hàng hiển thị số lượt tải còn lại trong tháng */}
                <div className="flex gap-2 border-b border-gray-100 py-2 items-center">
                  <span className="w-28 shrink-0 text-gray-500 text-xs">Hạn mức tháng</span>
                  <span className="min-w-0 flex-1 font-semibold text-xs">
                    {loadingQuota ? (
                      <span className="text-gray-400 font-normal">Đang kiểm tra...</span>
                    ) : accessQuota?.exempt ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-700 border border-emerald-200">
                        Không giới hạn lượt tải
                      </span>
                    ) : accessQuota ? (
                      <span
                        className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold border ${
                          isQuotaExhausted
                            ? "bg-red-50 text-red-700 border-red-200"
                            : accessQuota.remaining <= 3
                              ? "bg-amber-50 text-amber-700 border-amber-200"
                              : "bg-emerald-50 text-emerald-700 border-emerald-200"
                        }`}
                      >
                        {isQuotaExhausted
                          ? `Hết lượt (0 / ${accessQuota.allowance})`
                          : `Còn ${accessQuota.remaining} / ${accessQuota.allowance} lượt (Đã dùng ${accessQuota.usedThisMonth})`}
                      </span>
                    ) : (
                      <span className="text-gray-400 font-normal">—</span>
                    )}
                  </span>
                </div>
              </div>

              {/* Gợi ý tính năng Multi-download tải ngầm */}
              <div className="px-6 pb-2">
                <div className="rounded-2xl border border-red-100 bg-red-50/50 p-2.5 text-[11px] text-stone-600 leading-relaxed">
                  💡 Khi bấm <b>«Bắt đầu tải»</b>, tiến trình sẽ chuyển xuống góc phải màn hình để bạn có thể tiếp tục duyệt và chọn tải các bài học khác (multi download).
                </div>
              </div>

              {/* Footer action */}
              <div className="border-t border-gray-100 px-6 py-4 bg-gray-50/50">
                <div className="flex items-center justify-end gap-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="rounded-xl px-4 py-2.5 text-xs font-bold text-gray-600 hover:bg-gray-100 transition"
                  >
                    Hủy
                  </button>
                  <button
                    type="button"
                    disabled={isQuotaExhausted || isAlreadyDownloading}
                    onClick={handleStart}
                    className={`rounded-xl px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md transition ${
                      isQuotaExhausted || isAlreadyDownloading
                        ? "cursor-not-allowed bg-gray-300 shadow-none text-gray-500"
                        : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 shadow-red-200"
                    }`}
                  >
                    {isAlreadyDownloading
                      ? "Đang tải ở góc màn hình"
                      : isQuotaExhausted
                        ? "Hết lượt tải trong tháng"
                        : localVersionReady
                          ? "Tải lại vào máy"
                          : "Bắt đầu tải"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

function Row({ label, value }) {
  return (
    <div className="flex gap-2 border-b border-gray-100 py-1.5 items-center">
      <span className="w-28 shrink-0 text-gray-500 text-xs">{label}</span>
      <span className="min-w-0 flex-1 font-semibold text-gray-900 text-xs truncate">
        {value}
      </span>
    </div>
  );
}

export default LessonDownloadModal;
